import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import ImageComparison from './ImageComparison';

const VibeComparison = ({ className = '' }) => {
  const { t } = useTranslation();
  const [activeStyle, setActiveStyle] = useState('fresh');
  const [sliderValue, setSliderValue] = useState(3);
  const [showTooltip, setShowTooltip] = useState(false);

  // 风格数据配置
  const styleData = {
    fresh: {
      label: t('vibe.styles.fresh'),
      images: [
        '/i_r_0.5.jpg',
        '/i_r_0.6.jpg',
        '/i_r_0.7.jpg',
        '/i_r_0.8.jpg',
        '/i_r_0.9.jpg',
        '/i_r_1.0.jpg',
      ]
    },
    heroic: {
      label: t('vibe.styles.heroic'),
      images: [
        '/i_r_sharp_0.5.jpg',
        '/i_r_sharp_0.6.jpg',
        '/i_r_sharp_0.7.jpg',
        '/i_r_sharp_0.8.jpg',
        '/i_r_sharp_0.9.jpg',
        '/i_r_sharp_1.0.jpg',
      ]
    },
    more: {
      label: t('vibe.styles.more'),
      images: []
    }
  };

  const beforeImage = '/3_before.jpg'; // 固定的before图片
  
  // 获取当前风格的图片数组
  const currentImages = styleData[activeStyle]?.images || [];
  const maxSliderValue = Math.max(0, currentImages.length - 1);
  const currentAfterImage = currentImages[sliderValue] || '/1_after.jpg';

  // 动态文案生成函数
  const getDynamicText = () => {
    const midPoint = Math.floor(currentImages.length / 2);
    if (sliderValue < midPoint) {
      return t('vibe.slider.light_beauty');
    } else {
      return t('vibe.slider.strong_beauty');
    }
  };

  const currentStyleData = styleData[activeStyle];

  const handleStyleChange = (style) => {
    setActiveStyle(style);
    setSliderValue(3); // 切换风格时重置slider
  };

  const handleSliderChange = (e) => {
    setSliderValue(parseInt(e.target.value));
  };

  return (
    <div className={`space-y-8 ${className}`}>
      {/* 风格选择Tab */}
      <motion.div 
        className="flex justify-center px-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
      >
        <div className="relative inline-flex bg-white/80 backdrop-blur-sm rounded-full p-1 border border-slate-200 shadow-lg max-w-full overflow-x-auto scrollbar-hide">
          {Object.entries(styleData).map(([key, data], index) => (
            <div key={key} className="relative flex-shrink-0">
              {/* 滑动背景指示器 - 为每个按钮单独渲染 */}
              {activeStyle === key && (
                <motion.div
                  className="absolute inset-0 bg-pink-500 rounded-full shadow-lg"
                  layoutId="activeTabIndicator"
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 30
                  }}
                />
              )}
              
              <motion.button
                onClick={() => {
                  if (key !== 'more') {
                    handleStyleChange(key);
                  }
                }}
                onMouseEnter={() => {
                  if (key === 'more') {
                    setShowTooltip(true);
                  }
                }}
                onMouseLeave={() => {
                  if (key === 'more') {
                    setShowTooltip(false);
                  }
                }}
                className={`relative z-10 px-4 sm:px-6 py-2 sm:py-3 rounded-full text-xs sm:text-sm font-medium transition-colors duration-300 whitespace-nowrap flex items-center justify-center ${
                  activeStyle === key
                    ? 'text-white'
                    : key === 'more'
                    ? 'text-slate-400 cursor-default'
                    : 'text-slate-700 hover:text-slate-900'
                }`}
              >
                {data.label}
              </motion.button>
              
              {/* Tooltip for "更多..." */}
              <AnimatePresence>
                {key === 'more' && showTooltip && (
                  <motion.div
                    initial={{ opacity: 0, x: -10, scale: 0.8 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: -10, scale: 0.8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-full top-1/2 transform -translate-y-1/2 ml-2 px-3 py-2 bg-black text-white text-sm rounded-lg whitespace-nowrap z-50 shadow-xl border border-gray-600"
                    style={{ pointerEvents: 'none' }}
                  >
                    {t('vibe.tooltip')}
                    <div className="absolute right-full top-1/2 transform -translate-y-1/2 border-4 border-transparent border-r-black"></div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </motion.div>

      {/* 图片对比区域 */}
      <div className="max-w-4xl mx-auto">
        <ImageComparison 
          beforeImage={beforeImage}
          afterImage={currentAfterImage}
          className="w-full"
          initialAspectRatio={1470/1822}
          maxWidthPx={820}
          minWidthPx={320}
          maxHeightVh={68}
          smallScreenMaxHeightVh={56}
        />
      </div>

      {/* 强度调节Slider */}
      <motion.div 
        className="max-w-md mx-auto space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.8, ease: "easeOut" }}
      >
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9, ease: "easeOut" }}
        >
          <motion.h4 
            className="text-lg font-medium text-slate-700 mb-2"
            key={getDynamicText()} // 添加key来触发重新渲染动画
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {getDynamicText()}
          </motion.h4>
          <motion.p 
            className="text-sm text-slate-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 1.0 }}
          >
            {t('vibe.slider.description')}
          </motion.p>
        </motion.div>
        
        <motion.div 
          className="relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.1, ease: "easeOut" }}
        >
          {/* 自定义滑块轨道 */}
          <div className="relative h-2 bg-slate-200 rounded-full overflow-hidden shadow-inner">
            {/* 进度条 */}
            <motion.div
              className="h-full bg-gradient-to-r from-pink-400 to-pink-600 rounded-full"
              style={{ 
                width: maxSliderValue > 0 ? `${(sliderValue / maxSliderValue) * 100}%` : '0%'
              }}
              initial={{ width: 0 }}
              animate={{ 
                width: maxSliderValue > 0 ? `${(sliderValue / maxSliderValue) * 100}%` : '0%'
              }}
              transition={{ duration: 0.3, ease: "easeOut" }}
            />
          </div>
          
          {/* 滑块输入 */}
          <input
            type="range"
            min="0"
            max={maxSliderValue}
            value={sliderValue}
            onChange={handleSliderChange}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
          
          {/* 滑块手柄 */}
          <motion.div
            className="absolute top-1/2 w-6 h-6 bg-white rounded-full shadow-lg border-2 border-pink-400 transform -translate-y-1/2 cursor-pointer pointer-events-none"
            style={{ 
              left: maxSliderValue > 0 
                ? `calc(${(sliderValue / maxSliderValue) * 100}% - ${(sliderValue / maxSliderValue) * 24}px)`
                : '0px'
            }}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.9 }}
          />
        </motion.div>
        
        {/* 强度指示点 */}
        <motion.div 
          className="flex justify-between px-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 1.2, ease: "easeOut" }}
        >
          {currentStyleData.images.map((_, index) => (
            <motion.div
              key={index}
              className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                index === sliderValue ? 'bg-pink-400' : 'bg-slate-300'
              }`}
              whileHover={{ scale: 1.3 }}
            />
          ))}
        </motion.div>
        
        {/* 强度标签 */}
        <motion.div 
          className="flex justify-between text-xs text-slate-600 px-1"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 1.3 }}
        >
          <span>自然</span>
          <span>强烈</span>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default VibeComparison;